import { useMemo } from 'react'

import { ImageProps } from '@src/quince-components/src'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import { GenderName } from '@src/quince-site-common/src/utils/getCurrentPageGenderContext'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'

import { getImagesForOptions } from '../product-card.helpers'
import { CardVariant } from '../product-card.types'

export function useProductImageProps(
  selectedCardVariant?: CardVariant,
  gender?: GenderName,
  isMobile?: boolean,
  isHovered?: boolean,
  showSecondImageOnProductTiles?: boolean,
  showMScroll?: boolean,
) {
  const productImageProps = useMemo(() => {
    const firstImageIndex = showSecondImageOnProductTiles ? 1 : 0
    const secondImageIndex = showSecondImageOnProductTiles ? 0 : 1
    const images = getImagesForOptions(selectedCardVariant, gender)

    if (isMobile && showMScroll) {
      // If mScroll is enabled and mobile, return all available images for scrolling
      const scrollImages: ImageProps[] = images.map(img => ({
        url: img.url,
        description: '',
        id: img.id,
        height: img.height,
        width: img.width,
        alt: img.alt || 'image',
      }))
      return scrollImages
    }

    const firstImage = images[firstImageIndex]
    const secondImage = images[secondImageIndex] ?? images[firstImageIndex]
    const selectedImage = isMobile ? firstImage : isHovered ? secondImage : firstImage

    if (!selectedImage) {
      captureException(new Error(`No images found for selected variant in product card`), {
        module: MODULES.PRODUCT_CARD,
        feature: FEATURES.IMAGE,
      })
      return {
        url: '',
        title: '',
      }
    }

    const imageProps: ImageProps = {
      url: selectedImage.url,
      description: '',
      id: selectedImage.id,
      height: selectedImage.height,
      width: selectedImage.width,
      alt: selectedImage.alt || 'image',
    }

    return imageProps
  }, [showSecondImageOnProductTiles, selectedCardVariant, gender, isMobile, isHovered])
  return productImageProps
}
